import { getSubdomainFromHost, getSubdomainCatalogUrl } from './subdomain'
import { canUseFeature, normalizePlanId } from './plans'

const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'umkmindonesia.my.id'

export function getCatalogUrl(slug: string, useSubdomain = false, subdomainKey?: string | null) {
  if (useSubdomain) return getSubdomainCatalogUrl(subdomainKey || slug)
  return `${typeof window !== 'undefined' ? window.location.origin : `https://${BASE_DOMAIN}`}/catalog/${slug}`
}

/** URL publik katalog — subdomain untuk Premium jika tersedia */
export function getPublicCatalogHref(brand: {
  slug: string
  subdomain?: string | null
  plan?: string | null
  subdomainUrl?: string | null
  catalogUrl?: string | null
}): string {
  if (brand.catalogUrl) return brand.catalogUrl
  if (brand.subdomainUrl) return brand.subdomainUrl
  const plan = normalizePlanId(brand.plan)
  if (canUseFeature(plan, 'subdomain')) {
    return getSubdomainCatalogUrl(brand.subdomain || brand.slug)
  }
  return `/catalog/${brand.slug}`
}

export function isExternalCatalogHref(href: string) {
  return href.startsWith('http://') || href.startsWith('https://')
}

export function getProductCatalogUrl(brandSlug: string, productSlug: string) {
  return `/catalog/${brandSlug}/${productSlug}`
}

/**
 * Resolve image URL for display.
 * - Rewrites any .../uploads/... path to current site origin (fixes legacy domain & path salah)
 * - Relative paths → absolute dari origin
 */
export function getImageUrl(url?: string | null): string | null {
  if (!url) return null
  const trimmed = url.trim()
  if (!trimmed) return null

  const uploadsMatch = trimmed.match(/\/uploads\/(.+)$/i)
  if (uploadsMatch && typeof window !== 'undefined') {
    return `${window.location.origin}/uploads/${uploadsMatch[1]}`
  }

  if (trimmed.startsWith('http://')) return trimmed.replace('http://', 'https://')
  if (trimmed.startsWith('https://')) return trimmed

  if (typeof window !== 'undefined') {
    return trimmed.startsWith('/') ? `${window.location.origin}${trimmed}` : `${window.location.origin}/${trimmed}`
  }

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

/** Alias — perilaku sama dengan getImageUrl */
export function resolveUploadImageUrl(url?: string | null): string | null {
  return getImageUrl(url)
}

/** Wait until all <img> inside element have loaded (or failed). */
export function waitForImages(container: HTMLElement, timeoutMs = 8000): Promise<void> {
  const images = Array.from(container.querySelectorAll('img'))
  if (images.length === 0) return Promise.resolve()

  return new Promise((resolve) => {
    let pending = images.length
    const done = () => {
      pending -= 1
      if (pending <= 0) resolve()
    }

    const timer = setTimeout(resolve, timeoutMs)

    images.forEach((img) => {
      if (img.complete) {
        done()
        return
      }
      img.onload = done
      img.onerror = done
    })

    Promise.resolve().then(() => {
      if (pending <= 0) {
        clearTimeout(timer)
        resolve()
      }
    })
  })
}

export function formatRupiah(price: number | string | undefined) {
  if (!price) return 'Rp 0'
  const num = typeof price === 'string' ? parseInt(price, 10) : price
  if (isNaN(num)) return 'Rp 0'
  return `Rp ${num.toLocaleString('id-ID').replace(/,/g, '.')}`
}
